import { Text, TextList, TextListItem } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { EuTyreLabel } from '~/app/pages/configurator/components/more-information-dialog/components/eu-tyre-label/eu-tyre-label';
import { MoreInformationDialogHeader } from '~/app/pages/configurator/components/more-information-dialog/components/more-information-dialog-header';
import { TextFromHtml } from '~/app/pages/configurator/components/more-information-dialog/components/text-from-html';
import { useDebugMode } from '~/app/utils/debug-mode';
import { ConfiguredCarFeatureFragment, ConfiguredCarPackageFeatureFragment } from '~/graphql/__generated__/graphql';
import { CatalogItemType, ContentOrPackage } from '~/graphql/types';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { sanitizeText } from '~/utils/sanitize-text';
import { PackageContent } from '../package-content/package-content';
import { RegisterOrUpdateRateFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';

type MoreInformationProperties = {
	headline: string;
	imageUrl: string | null | undefined;
	imageUrls: string[];
	introText: string | null | undefined;
	description: string | null | undefined;
	benefits: string[] | null | undefined;
	featureText: string | null | undefined;
	remarks: string | null | undefined;
};

export function getItemInformation(featureOrPackage: ContentOrPackage): MoreInformationProperties {
	const { texts } = featureOrPackage;
	const defaultInfo: MoreInformationProperties = {
		headline: sanitizeText(featureOrPackage.name ?? ''),
		imageUrl:
			featureOrPackage.__typename === 'ConfiguredCarPackageFeature'
				? featureOrPackage?.images?.smallImageHD?.url || featureOrPackage?.images?.outerTileHD?.url
				: GetUrlFromImageSet(featureOrPackage?.imageSets, 'small_hd_image') ||
					GetUrlFromImageSet(featureOrPackage?.imageSets, 'outer_tile_hd') ||
					GetUrlFromImageSet(featureOrPackage?.imageSets, 'hd_thumbnail'),
		imageUrls: [],
		introText: texts?.intro,
		description: texts?.akText,
		benefits: texts?.benefits as string[],
		featureText: texts?.akText,
		remarks: texts?.akRemarks,
	};

	if (featureOrPackage.__typename === 'ConfiguredCarPackage') {
		if (featureOrPackage.featureGroup === 'RIMS') {
			defaultInfo.imageUrl = GetUrlFromImageSet(featureOrPackage.imageSets, 'wheel_image');
		}
		if (featureOrPackage.featureGroup === 'INTERIOR_TILES') {
			defaultInfo.imageUrl = GetUrlFromImageSet(featureOrPackage.imageSets, 'interior_tile_button');
			defaultInfo.featureText = featureOrPackage?.content?.at(0)?.texts?.akText;
		}
	}

	return defaultInfo;
}

export function MoreInformationDialogContent({
	featureOrPackage,
	onAddToConfiguration,
	hidePriceAndAvailability,
	setSubItem,
}: {
	featureOrPackage: ContentOrPackage;
	onAddToConfiguration: (() => void) | undefined;
	hidePriceAndAvailability?: boolean;
	setSubItem?: (item: ConfiguredCarFeatureFragment | ConfiguredCarPackageFeatureFragment) => void;
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const vatConfig = useVATConfig();
	const { getName } = useDebugMode();
	const { imageUrl, imageUrls, headline, featureText, introText, benefits, remarks } =
		getItemInformation(featureOrPackage);

	const mockFeature = {
		price: null,
		rate: null,
		featureGroup: null,
		pr7: null,
		tyreLabels: null,
		...featureOrPackage,
	};

	// https://collaboration.msi.audi.com/jira/browse/NWM-159
	// Onegraph sometimes returns benefits and tyre labels with null values
	const sanitizedBenefits = (benefits ?? []).filter(Boolean);
	const sanitizedTyreLabels = (mockFeature.tyreLabels ?? []).filter((label) => !!label?.tyreLabelUrl);
	// FIXME: should be replaced when the new financing-footnote-replacement is made
	if (
		config.enableFinancingServices &&
		!config.hideMonthlyRates &&
		config.showFinancingDisclaimerInDialogs &&
		!mockFeature.status.standard
	) {
		RegisterOrUpdateRateFootnoteForDialog();
	}

	return (
		<div className="flex flex-col gap-12">
			<MoreInformationDialogHeader
				headline={getName({ name: headline, pr7: mockFeature.pr7 ?? mockFeature.contentId }) ?? ''}
				price={mockFeature.price}
				rate={mockFeature.rate}
				vatConfig={vatConfig.feature}
				status={mockFeature.status}
				onAddToConfiguration={onAddToConfiguration}
				imageUrl={imageUrl}
				imageUrls={imageUrls}
				imageFit={mockFeature.featureGroup === 'RIMS' ? 'object-contain' : 'object-cover'}
				hidePriceAndAvailability={hidePriceAndAvailability}
				disclaimerStartDate={mockFeature.texts?.disclaimerStartDate || ''}
				disclaimerEndDate={mockFeature.texts?.disclaimerEndDate || ''}
				disclaimerHeadline={mockFeature.texts?.disclaimerHeadline || ''}
				disclaimerContent={mockFeature.texts?.disclaimerText || ''}
			/>
			{introText && (
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					<span dangerouslySetInnerHTML={{ __html: introText }} />
				</Text>
			)}
			{sanitizedBenefits.length > 0 && (
				<section className="flex flex-col gap-4">
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
						<span dangerouslySetInnerHTML={{ __html: i18n('ngc.dialogs.benefits') }} />
					</Text>
					<TextList size="md" variant={'bullet'}>
						{sanitizedBenefits.map((benefit, index) => (
							<TextListItem key={index}>
								<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
									<span dangerouslySetInnerHTML={{ __html: benefit }} />
								</Text>
							</TextListItem>
						))}
					</TextList>
				</section>
			)}
			{featureText && (
				<section className="flex flex-col gap-4">
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
						<span dangerouslySetInnerHTML={{ __html: i18n('ngc.dialogs.features') }} />
					</Text>

					<StyledDescription>
						<TextFromHtml html={featureText} />
					</StyledDescription>
				</section>
			)}
			{featureOrPackage.__typename === 'ConfiguredCarPackage' &&
				featureOrPackage.content &&
				featureOrPackage.content.length > 0 && (
					<PackageContent setSubItem={setSubItem} packageData={featureOrPackage} />
				)}
			{remarks && (
				<section className="bg-grey-5 p-4">
					<TextFromHtml html={remarks} />
				</section>
			)}
			{sanitizedTyreLabels.length > 0 && <EuTyreLabel tyreLabels={sanitizedTyreLabels} />}
		</div>
	);
}

export function MoreInformationTrimlineDialogContent({
	trimline,
	onAddToConfiguration,
}: {
	trimline: CatalogItemType['trimline'];
	onAddToConfiguration: (() => void) | undefined;
}) {
	const vatConfig = useVATConfig();
	const imageUrls = trimline.__item.images.filter((img) => img.width > 680 && img.width < 800).map((itm) => itm.url);
	const imageUrl = imageUrls[0] || trimline.__item.images[11]?.url;
	const headline = trimline.name;
	const introText = trimline.__item.teaserText;
	const description = trimline.__item.description;
	const remarks = trimline.__item.remarks;

	return (
		<div className="flex flex-col gap-12">
			<MoreInformationDialogHeader
				headline={headline}
				imageUrl={imageUrl}
				imageUrls={imageUrls}
				price={trimline.totalPrice}
				rate={trimline.monthlyRate}
				vatConfig={vatConfig.trimlineAndEngine}
				status={trimline.status}
				onAddToConfiguration={onAddToConfiguration}
			/>

			{introText && (
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					<span dangerouslySetInnerHTML={{ __html: introText }} />
				</Text>
			)}
			{description && (
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }} className="more-info-text">
					<StyledDescription>
						<span dangerouslySetInnerHTML={{ __html: description }} />
					</StyledDescription>
				</Text>
			)}
			{remarks && (
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }} className="more-info-text">
					<StyledRemarks>
						<span dangerouslySetInnerHTML={{ __html: remarks }} />
					</StyledRemarks>
				</Text>
			)}
		</div>
	);
}

const StyledDescription = styled.div`
	ul {
		margin-top: 1rem;
		margin-bottom: 1rem;
		list-style: disc;
		list-style-position: inside;
		text-indent: calc(-1rem - 5px);
		margin-left: calc(1rem + 5px);
	}
`;

const StyledRemarks = styled.div`
	p {
		margin-bottom: 1rem;
		font-size: 0.8em;
	}
`;
